import { useCallback, useDeferredValue, useRef } from "react";
import { useSettingsStore } from "../../stores/index.js";
import { HOUR_HEIGHT_MAX, HOUR_HEIGHT_MIN } from "../../stores/settings.store.js";

const ZOOM_STEP_PX = 8;

/**
 * Vertical zoom of the week/day grid. `hourHeight` is the persisted px-per-hour;
 * `onZoomWheel` handles Shift+scroll (up = zoom in, down = zoom out) and leaves
 * plain scroll alone so the grid still scrolls normally.
 *
 * The returned `hourHeight` is DEFERRED (`useDeferredValue`): each wheel tick
 * re-renders every mounted day column, and at urgent priority a fast gesture
 * saturated the main thread (queued wheel events, stuttery zoom). zustand
 * updates render synchronously (`useSyncExternalStore` opts out of transitions),
 * so deferring at the consumer is what restores time-slicing: the urgent render
 * keeps the old height (columns memo-skip, ~free) and React renders the grid at
 * the LATEST height concurrently, coalescing intermediate ticks. `pending`
 * steps the gesture math from the newest target, not the last rendered value.
 *
 * `minPx` is a DYNAMIC zoom floor (the caller passes `viewportHeight / 24`):
 * the returned height never drops below it, so the 24h track always fills the
 * scroll viewport — 23:59 stays pinned to the page bottom — and zoom-out stops
 * there. The floor is render-time only (it changes with the window); the
 * persisted setting keeps its static clamp.
 */
export function useCalendarZoom(minPx = HOUR_HEIGHT_MIN): {
  hourHeight: number;
  onZoomWheel: (e: React.WheelEvent) => void;
  /** Programmatic zoom (pinch gesture) — same clamps/persist as the wheel path. */
  zoomTo: (px: number) => void;
} {
  const liveHeight = useSettingsStore((s) => s.calendarHourHeight);
  const hourHeight = useDeferredValue(liveHeight);
  const setHeight = useSettingsStore((s) => s.setCalendarHourHeight);
  const effMin = Math.max(HOUR_HEIGHT_MIN, minPx);
  // Track the in-flight value so a fast wheel gesture steps from the latest
  // target, not the last committed (async) state.
  const pending = useRef(liveHeight);
  pending.current = liveHeight;

  const onZoomWheel = useCallback((e: React.WheelEvent) => {
    if (!e.shiftKey) return;
    e.preventDefault();
    const dir = e.deltaY > 0 ? -1 : 1;
    // Step from the DISPLAYED height — a persisted value below the current
    // floor would otherwise need several dead zoom-in ticks to catch up.
    const base = Math.max(pending.current, effMin);
    const next = Math.min(HOUR_HEIGHT_MAX, Math.max(effMin, base + dir * ZOOM_STEP_PX));
    if (next === pending.current) return;
    pending.current = next;
    void setHeight(next);
  }, [setHeight, effMin]);

  const zoomTo = useCallback((px: number) => {
    const next = Math.min(HOUR_HEIGHT_MAX, Math.max(effMin, Math.round(px)));
    if (next === pending.current) return;
    pending.current = next;
    void setHeight(next);
  }, [setHeight, effMin]);

  return { hourHeight: Math.max(hourHeight, effMin), onZoomWheel, zoomTo };
}
